import axios from 'axios';
import { authService } from './authService';

const PHOTO_SERVICE_BASE_URL = import.meta.env.VITE_PHOTO_SERVICE_URL || 'https://photo.agendario.com.br';
const UPLOAD_API_BASE_URL = import.meta.env.VITE_UPLOAD_API_URL || 'https://upload.agendario.com.br';

// Instância axios para o PhotoService (fotos de profissionais)
const photoApi = axios.create({
  baseURL: PHOTO_SERVICE_BASE_URL,
  headers: { 'Content-Type': 'multipart/form-data' },
});

photoApi.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Instância axios para o FileService (imagens de entidades: services, pages, etc.)
const uploadApi = axios.create({
  baseURL: UPLOAD_API_BASE_URL,
  headers: { 'Content-Type': 'multipart/form-data' },
});

uploadApi.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Cache-busting via localStorage ──────────────────────────────────────────
const PHOTO_TIMESTAMPS_KEY = 'professional_photo_timestamps';
const SERVICE_PHOTO_TIMESTAMPS_KEY = 'service_photo_timestamps';

function getPhotoTimestamps() {
  try {
    const stored = localStorage.getItem(PHOTO_TIMESTAMPS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function getPhotoTimestamp(professionalId) {
  return getPhotoTimestamps()[professionalId] || null;
}

function savePhotoTimestamp(professionalId, timestamp) {
  const timestamps = getPhotoTimestamps();
  timestamps[professionalId] = timestamp;
  localStorage.setItem(PHOTO_TIMESTAMPS_KEY, JSON.stringify(timestamps));
}

function getServicePhotoTimestamps() {
  try {
    const stored = localStorage.getItem(SERVICE_PHOTO_TIMESTAMPS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function getServicePhotoTimestamp(serviceId) {
  return getServicePhotoTimestamps()[serviceId] || null;
}

function saveServicePhotoTimestamp(serviceId, timestamp) {
  const timestamps = getServicePhotoTimestamps();
  timestamps[serviceId] = timestamp;
  localStorage.setItem(SERVICE_PHOTO_TIMESTAMPS_KEY, JSON.stringify(timestamps));
}

/**
 * Serviço de integração com PhotoService API
 * Upload, obtenção e deleção de fotos de perfil
 */
const photoService = {
  /**
   * Faz upload de foto de perfil do profissional
   * @param {File} file - Arquivo de imagem (já cropado)
   * @param {string} professionalId - ID do profissional
   * @returns {Promise<Object>} - { photoId, photoUrl, thumbnailUrl, originalFileSize, processedFileSize, thumbnailFileSize, dimensions, uploadedAt }
   */
  async uploadProfilePhoto(file, professionalId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('professionalId', professionalId);
    formData.append('generateThumbnail', 'false'); // Não precisamos de thumbnail
    formData.append('quality', '85');
    formData.append('maxSize', '800');

    const response = await photoApi.post('/api/photo/upload', formData);

    return response.data;
  },

  /**
   * Obtém URL da foto de perfil do usuário autenticado
   * @returns {Promise<Object>} - { userId, photoUrl }
   */
  async getMyProfilePhoto() {
    try {
      const response = await photoApi.get('/api/photo/my-photo');
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Foto não encontrada
      }
      throw error;
    }
  },

  /**
   * Obtém URL da foto de perfil de um usuário específico
   * @param {string} userId - ID do usuário
   * @returns {Promise<Object>} - { userId, photoUrl }
   */
  async getUserProfilePhoto(userId) {
    try {
      const response = await photoApi.get(`/api/photo/user/${userId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Foto não encontrada
      }
      throw error;
    }
  },

  /**
   * Deleta foto de perfil do usuário autenticado
   * @returns {Promise<void>}
   */
  async deleteMyProfilePhoto() {
    await photoApi.delete('/api/photo/my-photo');
  },

  /**
   * Valida arquivo de imagem sem fazer upload
   * @param {File} file - Arquivo a validar
   * @returns {Promise<Object>} - { isValid, fileName, size, sizeKB, contentType, maxSizeMB }
   */
  async validateImage(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await photoApi.post('/api/photo/validate', formData);

    return response.data;
  },

  /**
   * Gera URL completa da foto de perfil do profissional (com cache-busting)
   * @param {string} userId - ID do usuário (namespace)
   * @param {string} professionalId - ID do profissional
   * @returns {string} - URL da foto com ?v=timestamp se houver timestamp salvo
   */
  getProfilePhotoUrl(userId, professionalId) {
    const photoCdnUrl = import.meta.env.VITE_PHOTO_CDN_URL || 'https://photos.agendario.com.br';
    const baseUrl = `${photoCdnUrl}/${userId}/${professionalId}.jpg`;
    const ts = getPhotoTimestamp(professionalId);
    return ts ? `${baseUrl}?v=${ts}` : baseUrl;
  },

  getPhotoTimestamp,
  savePhotoTimestamp,

  /**
   * Faz upload de imagem de um serviço via FileService.
   * POST /api/images com entityType=service
   * Path no bucket: service/{serviceId}.jpg
   * CDN: images.agendario.com.br/service/{serviceId}.jpg
   * @param {File} file - Arquivo de imagem (já cropado)
   * @param {string} serviceId - ID do serviço
   * @returns {Promise<Object>} - { fileId, fileUrl, ... }
   */
  async uploadServicePhoto(file, serviceId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityId', serviceId);
    formData.append('entityType', 'service');

    const response = await uploadApi.post('/api/images', formData);
    return response.data;
  },

  /**
   * Gera URL da imagem de um serviço (CDN com cache-busting)
   * CDN: images.agendario.com.br/service/{serviceId}.jpg
   */
  getServicePhotoUrl(serviceId) {
    const imagesCdnUrl = import.meta.env.VITE_IMAGES_CDN_URL || 'https://images.agendario.com.br';
    const baseUrl = `${imagesCdnUrl}/service/${serviceId}.jpg`;
    const ts = getServicePhotoTimestamp(serviceId);
    return ts ? `${baseUrl}?v=${ts}` : baseUrl;
  },

  getServicePhotoTimestamp,
  saveServicePhotoTimestamp,
};

export default photoService;
